import { useEffect, useMemo, useState } from 'react';
import { useT } from '../context/LocaleContext';
import { formatWhen, localeTag } from '../lib/format';
import {
  dateInputValue,
  fetchBookingSlotOptions,
  parseDateInput,
  updateBookingSchedule,
} from '../lib/bookingSlots';

function formatSlotTime(value) {
  return new Intl.DateTimeFormat(localeTag(), {
    hour: 'numeric',
    minute: '2-digit',
  }).format(value);
}

function sameInstant(a, b) {
  return a && b && new Date(a).getTime() === new Date(b).getTime();
}

export default function BookingReschedulePanel({
  booking,
  onRescheduled,
  embedded = false,
}) {
  const t = useT();
  const initialDay = booking?.scheduled_at
    ? dateInputValue(new Date(booking.scheduled_at))
    : dateInputValue();
  const [dayValue, setDayValue] = useState(initialDay);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(
    booking?.scheduled_at ? new Date(booking.scheduled_at) : null
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const durationMinutes = Number(booking?.estimated_minutes) || 60;
  const minDay = dateInputValue();

  const availableSlots = useMemo(
    () => slots.filter((slot) => slot.isAvailable),
    [slots]
  );

  useEffect(() => {
    if (!booking?.branch_id || !dayValue) return undefined;

    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const day = parseDateInput(dayValue);
        const options = await fetchBookingSlotOptions({
          branchId: booking.branch_id,
          day,
          durationMinutes,
          mechanicId: booking.preferred_mechanic_id || null,
          vehicleId: booking.vehicle_id || null,
          ignoreBookingId: booking.id,
        });
        if (!alive) return;
        setSlots(options);
        const current = booking.scheduled_at ? new Date(booking.scheduled_at) : null;
        const stillValid =
          current &&
          options.some(
            (slot) => slot.isAvailable && sameInstant(slot.start, current)
          );
        setSelectedSlot(stillValid ? current : null);
      } catch (loadError) {
        if (!alive) return;
        setSlots([]);
        setSelectedSlot(null);
        setError(loadError.message || t('reschedule.loadFailed'));
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [
    booking?.branch_id,
    booking?.id,
    booking?.preferred_mechanic_id,
    booking?.scheduled_at,
    booking?.vehicle_id,
    dayValue,
    durationMinutes,
  ]);

  async function save() {
    if (!selectedSlot) return;
    setSaving(true);
    setError('');
    const { data, error: saveError } = await updateBookingSchedule(
      booking.id,
      selectedSlot.toISOString()
    );
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    onRescheduled?.(data.scheduled_at);
  }

  const unchanged =
    booking?.scheduled_at &&
    selectedSlot &&
    sameInstant(booking.scheduled_at, selectedSlot);

  return (
    <section className={embedded ? 'reschedule-panel reschedule-panel-embedded' : 'reschedule-panel'}>
      {!embedded ? (
        <header className="reschedule-panel-head">
          <h3>{t('reschedule.title')}</h3>
          <p className="lede">
            {t('reschedule.current', {
              when: formatWhen(booking.scheduled_at || booking.created_at),
            })}
          </p>
        </header>
      ) : null}

      <label className="reschedule-day">
        {t('reschedule.day')}
        <input
          type="date"
          min={minDay}
          value={dayValue}
          onChange={(event) => setDayValue(event.target.value)}
        />
      </label>

      {loading ? <p className="lede">{t('reschedule.loading')}</p> : null}

      {!loading && availableSlots.length === 0 ? (
        <p className="lede">{t('reschedule.empty')}</p>
      ) : null}

      {!loading && availableSlots.length > 0 ? (
        <div className="slot-grid" role="listbox" aria-label={t('reschedule.slots')}>
          {availableSlots.map((slot) => {
            const active = selectedSlot && sameInstant(slot.start, selectedSlot);
            return (
              <button
                key={slot.start.toISOString()}
                type="button"
                role="option"
                aria-selected={active}
                className={['slot-chip', active ? 'selected' : ''].filter(Boolean).join(' ')}
                onClick={() => setSelectedSlot(slot.start)}
              >
                {formatSlotTime(slot.start)}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="reschedule-actions">
        <button
          type="button"
          className="primary"
          disabled={saving || !selectedSlot || unchanged}
          onClick={save}
        >
          {saving ? t('common.saving') : t('reschedule.save')}
        </button>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
    </section>
  );
}
